package com.exura.features.publicplans

import com.exura.features.localization.domain.model.BasicLangSet
import com.exura.features.localization.domain.model.Language
import com.exura.features.localization.domain.model.Localization

interface PublicPlansLang {
    val planStatistics: PlanStatistics
    val planSharing: PublicPlanSharing
    val qr: QR
    val usage: PublicPlanUsage

    val publicPlansPage: String
    val swapTemplate: String
    val refreshStatistics: String
    val lockSharing: String
    val lockSharingUnlock: String
    val servingHistory: String
    val planUsage: String

    val publicPlans: String
    val noPublicPlansYet: String
    val makePlanPublicPrompt: String
    val detailsComingSoon: String
    val addPublicPlan: String
    val updateComplete: String

    val accessCode: String
    val active: String
    val inactive: String
    val sharing: String

    interface QR {
        val qrCode: String
        val scanCode: String
        val clickAround: String
    }

    interface PublicPlanSharing {
        val accessCode: String
        val active: String
        val inactive: String
        val sharing: String

        val firstPublish: String
        val availableFrom: String
    }

    interface PlanStatistics {
        val view: String
        val views: String
        fun views(number: Int): String
        fun viewsCount(number: Int): String = "$number ${views(number)}"

        val access: String
        val accesses: String
        fun accesses(number: Int): String
        fun accessesCount(number: Int): String = "$number ${accesses(number)}"

        val lastModification: String
        val lastView: String
        val lastAccess: String

        val neverViewed: String
        val neverAccessed: String
    }

    interface PublicPlanUsage {
        val range: String
        val rangeDay: String
        val rangeWeek: String
        val rangeMonth: String

        val started: String
        val finished: String
        val downloaded: String
        val inProgress: String

        val timeMarkToday: String
        val noDataToDisplay: String
    }
}

data object PublicPlansPolish : PublicPlansLang {
    override val planStatistics = PlanStatistics
    override val planSharing = PublicPlanSharing
    override val qr = QR
    override val usage = PublicPlanUsage

    override val publicPlansPage: String = "publiczna strona planu"
    override val swapTemplate: String = "Zastąp szablon"
    override val refreshStatistics: String = "Odśwież statystyki"
    override val lockSharing: String = "Zablokuj dostęp"
    override val lockSharingUnlock: String = "Odblokuj dostęp"
    override val servingHistory: String = "Historia udostepniania"
    override val planUsage: String = "Wykorzystanie planu"

    override val publicPlans: String = "Publiczne plany"
    override val noPublicPlansYet: String = "Brak publicznych planów"
    override val makePlanPublicPrompt: String = "Upublicznij plan, aby rozpocząć"
    override val detailsComingSoon: String = "Szczegóły wkrótce"
    override val addPublicPlan: String = "Dodaj publiczny plan"
    override val updateComplete: String = "Zaktualizowano"

    override val accessCode: String = "Kod dostępu:"
    override val active: String = "Włączone"
    override val inactive: String = "Wyłączone"
    override val sharing: String = "Udostępnianie"

    data object PublicPlanSharing : PublicPlansLang.PublicPlanSharing {
        override val accessCode: String = "Kod dostępu:"
        override val active: String = "Włączone"
        override val inactive: String = "Wyłączone"
        override val sharing: String = "Udostępnianie"

        override val firstPublish: String = "pierwsza publikacja"
        override val availableFrom: String = "dostępny od"
    }

    data object PlanStatistics : PublicPlansLang.PlanStatistics {
        override val view: String = "wyświetlenie"
        override val views: String = "wyświetleń"
        override fun views(number: Int): String = when {
            number == 1 -> "wyświetlenie"
            number in 2..4 -> "wyświetlenia"
            else -> "wyświetleń"
        }

        override val access: String = "pobranie"
        override val accesses: String = "pobrań"
        override fun accesses(number: Int): String = when {
            number == 1 -> "pobranie"
            number in 2..4 -> "pobrania"
            else -> "pobrań"
        }
        override val lastModification: String = "ostatnia zmiana"
        override val lastView: String = "ostatnie wyświetlenie"
        override val lastAccess: String = "ostatnie pobranie"

        override val neverViewed: String = "Nie wyświetlono"
        override val neverAccessed: String = "Nie pobrano"
    }

    data object QR : PublicPlansLang.QR {
        override val qrCode: String = "kod QR"
        override val scanCode: String = "Zeskanuj kod QR"
        override val clickAround: String = "Kliknij obok aby zamknąć"
    }

    data object PublicPlanUsage : PublicPlansLang.PublicPlanUsage {
        override val range: String = "Zakres"
        override val rangeDay: String = "Dzień"
        override val rangeWeek: String = "Tydzień"
        override val rangeMonth: String = "Miesiąc"

        override val started: String = "Rozpoczęto"
        override val finished: String = "Zakończono"
        override val downloaded: String = "Pobrano"
        override val inProgress: String = "W trakcie"

        override val timeMarkToday: String = "Dziś"
        override val noDataToDisplay: String = "Brak danych do wyświetlenia"
    }

}

data object PublicPlansEnglish : PublicPlansLang {
    override val planStatistics = PlanStatistics
    override val planSharing = PublicPlanSharing
    override val qr = QR
    override val usage = PublicPlanUsage

    override val publicPlansPage: String = "plan's public page"
    override val swapTemplate: String = "Swap template"
    override val refreshStatistics: String = "Refresh statistics"
    override val lockSharing: String = "Lock sharing"
    override val lockSharingUnlock: String = "Unlock sharing"
    override val servingHistory: String = "Sharing history"
    override val planUsage: String = "Plan's usage:"

    override val publicPlans: String = "Public Plans"
    override val noPublicPlansYet: String = "No public plans yet"
    override val makePlanPublicPrompt: String = "Make a plan public to get started"
    override val detailsComingSoon: String = "Details coming soon"
    override val addPublicPlan: String = "Add public plan"
    override val updateComplete: String = "Update complete"
    override val accessCode: String = "Access code"
    override val active: String = "Active"
    override val inactive: String = "Inactive"
    override val sharing: String = "Sharing"

    data object PublicPlanSharing : PublicPlansLang.PublicPlanSharing {
        override val accessCode: String = "Access code"
        override val active: String = "Active"
        override val inactive: String = "Inactive"
        override val sharing: String = "Sharing"

        override val firstPublish: String = "First published"
        override val availableFrom: String = "Available from"
    }

    data object PlanStatistics : PublicPlansLang.PlanStatistics {
        override val view: String = "view"
        override val views: String = "views"
        override fun views(number: Int): String = if (number == 1) "view" else "views"

        override val access: String = "download"
        override val accesses: String = "downloads"
        override fun accesses(number: Int): String = if (number == 1) "download" else "downloads"

        override val lastModification: String = "Last modified"
        override val lastView: String = "Last viewed"
        override val lastAccess: String = "Last accessed"

        override val neverViewed: String = "Never viewed"
        override val neverAccessed: String = "Never accessed"
    }

    data object QR : PublicPlansLang.QR {
        override val qrCode: String = "QR code"
        override val scanCode: String = "Scan the QR code"
        override val clickAround: String = "Click outside to close"
    }

    data object PublicPlanUsage : PublicPlansLang.PublicPlanUsage {
        override val range: String = "Range"
        override val rangeDay: String = "Day"
        override val rangeWeek: String = "Week"
        override val rangeMonth: String = "Month"

        override val started: String = "Started"
        override val finished: String = "Finished"
        override val downloaded: String = "Downloaded"
        override val inProgress: String = "In progress"

        override val timeMarkToday: String = "Today"
        override val noDataToDisplay: String = "No data to display"
    }
}

private val publicPlansLangSet = BasicLangSet<PublicPlansLang>(
    mapOf(
        Language.POLISH to PublicPlansPolish,
        Language.ENGLISH to PublicPlansEnglish,
    )
)

val publicPlansLocalization = Localization<PublicPlansLang>(publicPlansLangSet)
